import type { CommentFieldHandle } from "@/shared/components/comment-field/comment-field";
import type { CommentFieldValue } from "@/shared/components/comment-field/use-comment-field";
import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useControlResultActivityAttachmentForm } from "../hooks/use-control-result-activity-attachment-form";
import { useControlResultActivityCommentForm } from "../hooks/use-control-result-activity-comment-form";

export const useResultDetailActivityFormModel = (
  controlId: string,
  controlResultId: string,
  companyId: string,
) => {
  const { t } = useTranslation();
  const [isSubmittingForm, setIsSubmittingForm] = useState(false);
  const commentFieldRef = useRef<CommentFieldHandle | null>(null);

  const commentForm = useControlResultActivityCommentForm({
    controlId,
    controlResultId,
  });
  const attachmentForm = useControlResultActivityAttachmentForm({
    controlId,
    controlResultId,
  });

  const formReset = () => {
    attachmentForm.reset();
    commentForm.reset();

    commentFieldRef.current?.reset();
  };

  const handleSubmit = () => {
    if (isSubmittingForm) return; // Prevent multiple submissions

    const fileToUpload = attachmentForm.value;
    const commentToSubmit = commentForm.value;

    formReset();
    setIsSubmittingForm(true);

    // Submit to comment form, which will handle file upload and optimistic updates
    commentForm.submit({
      commentContent: commentToSubmit,
      fileToUpload,
      uploadFile: async (file) => {
        const result = await attachmentForm.submit({ file });
        return result?.id;
      },
      onSuccess: () => {
        setIsSubmittingForm(false);
      },
      onError: () => {
        setIsSubmittingForm(false);
      },
    });
  };

  return {
    t,
    form: {
      update: (values: CommentFieldValue) => {
        commentForm.update(values.content);
        attachmentForm.update(values.attachment);
      },
      valid: commentForm.valid || attachmentForm.valid,
    },
    attachmentForm,
    isSubmittingForm,
    commentFieldRef,
    handleSubmit,
  };
};
